import * as React from 'react';
import { createStyles, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { NumberFormatValues } from 'react-number-format';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Components
import { PatternNumberInput } from './PatternNumberInput';
import { NumericFormatInput } from './NumericFormatInput';

// Types
import { GenericFieldProps } from '../types';
import { IPhoneNumber } from '../../../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        readonlyInput: commonFieldStyles.readonlyInput,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        radioGroup: {
            minHeight: '2.3rem',
            alignItems: 'center'
        },
        fieldGroup: {
            flex: 1,
            gap: '0'
        }
    };
});

export interface CustomPhoneNumberGroupProps extends GenericFieldProps {
    key?: string;
    error?: string;
    value?: IPhoneNumber;
    onChange?: (val: IPhoneNumber) => void;
    readOnly?: boolean;
}

export const PhoneNumberGroup: React.FC<CustomPhoneNumberGroupProps> = ({
    readOnly,
    labelKey,
    value,
    onChange,
    ...props
}) => {
    const { classes } = useStyles();
    const { t } = useTranslation();

    const state = React.useMemo(
        () => ({
            areaCode: '000',
            number: value?.number?.toString?.(),
            ext: value?.ext
        }),
        [value]
    );

    return (
        <Group className={classes.fieldGroup} {...props}>
            <PatternNumberInput
                mask="#"
                value={value?.number}
                labelKey={labelKey}
                pattern={'(###) ### ####'}
                allowEmptyFormatting
                icon={<span>+1</span>}
                groupPosition={'l'}
                flexGrow
                error={props.error}
                onChange={(event) => {
                    onChange?.({
                        ...state,
                        number: event.target.value.toString()
                    });
                }}
            />
            <NumericFormatInput
                width="6rem"
                groupPosition={'r'}
                value={value?.ext}
                placeholder={t('extension', { ns: 'common' }) ?? ''}
                allowNegative={false}
                isAllowed={(values: NumberFormatValues) => {
                    const { floatValue } = values;
                    return typeof floatValue === 'undefined' || (floatValue >= 0 && floatValue <= 99999999);
                }}
                onChange={(value) => {
                    onChange?.({
                        ...state,
                        ext: value.toString()
                    });
                }}
            />
        </Group>
    );
};
